import { useCollection } from '@amzn/awsui-collection-hooks';
import Button from '@amzn/awsui-components-react/polaris/button';
import CollectionPreferences, {
    CollectionPreferencesProps,
} from '@amzn/awsui-components-react/polaris/collection-preferences';
import Header from '@amzn/awsui-components-react/polaris/header';
import Pagination from '@amzn/awsui-components-react/polaris/pagination';
import PropertyFilter from '@amzn/awsui-components-react/polaris/property-filter';
import Table, { TableProps } from '@amzn/awsui-components-react/polaris/table';
import {
    countText,
    EmptyMessage,
    getFilteringOptions,
    PropertyFilterConstants,
    StoragePartition,
    StoragePartitionKeys,
} from '@amzn/event-engine-js-utils';
import React, { useEffect, useState } from 'react';
import { ErrorMessage } from 'services';
import i18nStrings from 'utils/constants/i18nStrings';
import tableConfig from 'utils/constants/table-config';

import {
    columnDefinitions,
    defaultPreferences,
    defaultQuery,
    defaultSortPreferences,
    filteringProperties,
    STORAGE_SCOPE,
} from './config';
import {
    EventOutputsTableProps,
    ParticipantStackOutputTableItem,
} from './types';

const EventOutputsTable = ({
    outputs,
    error,
    loading,
}: EventOutputsTableProps) => {
    const [sorting, setSorting] = useState<
        TableProps.SortingState<ParticipantStackOutputTableItem>
    >(
        StoragePartition.getItem(
            StoragePartitionKeys.tableSortColumns,
            STORAGE_SCOPE
        ) || defaultSortPreferences
    );
    const [
        preferences,
        setPreferences,
    ] = useState<CollectionPreferencesProps.Preferences>(
        StoragePartition.getItem(
            StoragePartitionKeys.tablePreferences,
            STORAGE_SCOPE
        ) || defaultPreferences
    );
    const {
        items,
        actions,
        filteredItemsCount,
        collectionProps,
        propertyFilterProps,
        paginationProps,
    } = useCollection(outputs, {
        propertyFiltering: {
            filteringProperties,
            defaultQuery,
            empty: error ? (
                <EmptyMessage
                    title={i18nStrings.event.eventOutputsTable.errorTitle}
                    description={ErrorMessage.getMessage(error)}
                />
            ) : (
                <EmptyMessage
                    title={i18nStrings.event.eventOutputsTable.emptyTitle}
                    description={
                        i18nStrings.event.eventOutputsTable.emptyDescription
                    }
                />
            ),
            noMatch: (
                <EmptyMessage
                    title={i18nStrings.table.noMatches}
                    description={i18nStrings.table.noMatchesDescription}
                    actions={
                        <Button
                            onClick={() =>
                                actions.setPropertyFiltering(defaultQuery)
                            }>
                            {i18nStrings.table.clearFilters}
                        </Button>
                    }
                />
            ),
        },
        pagination: { pageSize: preferences.pageSize },
        sorting: {
            defaultState: sorting,
        },
    });
    useEffect(() => {
        StoragePartition.setItem(
            StoragePartitionKeys.tableSortColumns,
            sorting,
            STORAGE_SCOPE
        );
    }, [sorting]);
    useEffect(() => {
        StoragePartition.setItem(
            StoragePartitionKeys.tablePreferences,
            preferences,
            STORAGE_SCOPE
        );
    }, [preferences]);
    return (
        <Table
            {...collectionProps}
            header={
                <Header counter={`(${outputs.length})`}>
                    {i18nStrings.event.eventOutputsTable.title}
                </Header>
            }
            columnDefinitions={columnDefinitions}
            visibleColumns={preferences.visibleContent}
            items={items}
            pagination={
                <Pagination
                    {...paginationProps}
                    ariaLabels={tableConfig.paginationAriaLabels}
                />
            }
            filter={
                <PropertyFilter
                    {...propertyFilterProps}
                    i18nStrings={PropertyFilterConstants.i18nStrings}
                    filteringOptions={getFilteringOptions(
                        outputs,
                        filteringProperties
                    )}
                    countText={countText(
                        filteredItemsCount || 0,
                        i18nStrings.item
                    )}
                />
            }
            preferences={
                <CollectionPreferences
                    {...tableConfig.collectionPreferencesI18nConstants}
                    preferences={preferences}
                    onConfirm={({ detail }) => setPreferences(detail)}
                    visibleContentPreference={{
                        title: i18nStrings.table.selectVisibleColumns,
                        options: [
                            {
                                label: i18nStrings.table.properties,
                                options: columnDefinitions.map((column) => ({
                                    id: column.id as string,
                                    label: column.header as string,
                                    editable: column.editable,
                                })),
                            },
                        ],
                    }}
                    pageSizePreference={tableConfig.pageSizePreference}
                    wrapLinesPreference={tableConfig.wrapLinesPreference}
                />
            }
            loadingText={tableConfig.loadingText}
            loading={loading && !outputs.length}
            onSortingChange={(e) => {
                setSorting(e.detail);
                collectionProps.onSortingChange &&
                    collectionProps.onSortingChange(e);
            }}
            wrapLines={preferences.wrapLines}
        />
    );
};

export default EventOutputsTable;
